const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => {
  const token = localStorage.getItem('authToken');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const handleResponse = async (response) => {
  if (!response.ok) {
    if (response.status === 401) {
      // Token expired or invalid - redirect to login
      localStorage.clear();
      window.location.href = '/';
      throw new Error('Session expired. Please login again.');
    }
    const error = await response.json().catch(() => ({ detail: 'An error occurred' }));
    throw new Error(error.detail || 'Request failed');
  }
  return response.json();
};

// Auth APIs
export const authAPI = {
  login: async (username, password) => {
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    return handleResponse(response);
  },
  
  getMe: async () => {
    const response = await fetch(`${API_URL}/api/auth/me`, {
      headers: { ...getAuthHeader() },
    });
    return handleResponse(response);
  },
};

// User APIs
export const userAPI = {
  getAll: async () => {
    const response = await fetch(`${API_URL}/api/users`, {
      headers: { ...getAuthHeader() },
    });
    return handleResponse(response);
  },
  
  create: async (userData) => {
    const response = await fetch(`${API_URL}/api/users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(userData),
    });
    return handleResponse(response);
  },
  
  update: async (userId, userData) => {
    const response = await fetch(`${API_URL}/api/users/${userId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(userData),
    });
    return handleResponse(response);
  },
  
  delete: async (userId) => {
    const response = await fetch(`${API_URL}/api/users/${userId}`, {
      method: 'DELETE',
      headers: { ...getAuthHeader() },
    });
    return handleResponse(response);
  },
};

// Product APIs
export const productAPI = {
  getAll: async () => {
    const response = await fetch(`${API_URL}/api/products`, {
      headers: { ...getAuthHeader() },
    });
    return handleResponse(response);
  },
  
  create: async (productData) => {
    const response = await fetch(`${API_URL}/api/products`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(productData),
    });
    return handleResponse(response);
  },
  
  update: async (productId, productData) => {
    const response = await fetch(`${API_URL}/api/products/${productId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(productData),
    });
    return handleResponse(response);
  },
  
  delete: async (productId) => {
    const response = await fetch(`${API_URL}/api/products/${productId}`, {
      method: 'DELETE',
      headers: { ...getAuthHeader() },
    });
    return handleResponse(response);
  },
  
  getByBarcode: async (barcode) => {
    const response = await fetch(`${API_URL}/api/products/barcode/${barcode}`, {
      headers: { ...getAuthHeader() },
    });
    return handleResponse(response);
  },
};

// Category APIs
export const categoryAPI = {
  getAll: async () => {
    const response = await fetch(`${API_URL}/api/categories`, {
      headers: { ...getAuthHeader() },
    });
    return handleResponse(response);
  },
  
  create: async (categoryData) => {
    const response = await fetch(`${API_URL}/api/categories`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(categoryData),
    });
    return handleResponse(response);
  },
  
  update: async (categoryId, categoryData) => {
    const response = await fetch(`${API_URL}/api/categories/${categoryId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(categoryData),
    });
    return handleResponse(response);
  },
  
  delete: async (categoryId) => {
    const response = await fetch(`${API_URL}/api/categories/${categoryId}`, {
      method: 'DELETE',
      headers: { ...getAuthHeader() },
    });
    return handleResponse(response);
  },
};

// Invoice APIs
export const invoiceAPI = {
  getAll: async (date = null, startDate = null, endDate = null) => {
    let url = `${API_URL}/api/invoices`;
    const params = [];
    if (date) params.push(`date=${date}`);
    if (startDate) params.push(`start_date=${startDate}`);
    if (endDate) params.push(`end_date=${endDate}`);
    if (params.length > 0) url += '?' + params.join('&');
    
    const response = await fetch(url, {
      headers: { ...getAuthHeader() },
    });
    return handleResponse(response);
  },
  
  create: async (invoiceData) => {
    const response = await fetch(`${API_URL}/api/invoices`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(invoiceData),
    });
    return handleResponse(response);
  },
  
  getByInvoiceNo: async (invoiceNo) => {
    const response = await fetch(`${API_URL}/api/invoices/${invoiceNo}`, {
      headers: { ...getAuthHeader() },
    });
    return handleResponse(response);
  },
};

// Session APIs
export const sessionAPI = {
  start: async (openingCash) => {
    const response = await fetch(`${API_URL}/api/sessions/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({ opening_cash: openingCash }),
    });
    return handleResponse(response);
  },
  
  end: async (closingCash) => {
    const response = await fetch(`${API_URL}/api/sessions/end`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({ closing_cash: closingCash }),
    });
    return handleResponse(response);
  },
  
  getCurrent: async () => {
    const response = await fetch(`${API_URL}/api/sessions/current`, {
      headers: { ...getAuthHeader() },
    });
    return handleResponse(response);
  },
  
  getLastClosing: async () => {
    const response = await fetch(`${API_URL}/api/sessions/last-closing`, {
      headers: { ...getAuthHeader() },
    });
    return handleResponse(response);
  },

  getShiftLog: async () => {
    const response = await fetch(`${API_URL}/api/sessions/log`, {
      headers: { ...getAuthHeader() },
    });
    return handleResponse(response);
  },

  checkOpen: async () => {
    const response = await fetch(`${API_URL}/api/sessions/check-open`, {
      headers: { ...getAuthHeader() },
    });
    return handleResponse(response);
  },
};

// Cashout APIs
export const cashoutAPI = {
  getAll: async (date = null) => {
    const url = date ? `${API_URL}/api/cashouts?date=${date}` : `${API_URL}/api/cashouts`;
    const response = await fetch(url, {
      headers: { ...getAuthHeader() },
    });
    return handleResponse(response);
  },
  
  create: async (cashoutData) => {
    const response = await fetch(`${API_URL}/api/cashouts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(cashoutData),
    });
    return handleResponse(response);
  },
};

// Return APIs
export const returnAPI = {
  getAll: async () => {
    const response = await fetch(`${API_URL}/api/returns`, {
      headers: { ...getAuthHeader() },
    });
    return handleResponse(response);
  },
  
  create: async (returnData) => {
    const response = await fetch(`${API_URL}/api/returns`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(returnData),
    });
    return handleResponse(response);
  },
};

// Discard APIs
export const discardAPI = {
  getAll: async () => {
    const response = await fetch(`${API_URL}/api/discards`, {
      headers: { ...getAuthHeader() },
    });
    return handleResponse(response);
  },
  
  create: async (discardData) => {
    const response = await fetch(`${API_URL}/api/discards`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(discardData),
    });
    return handleResponse(response);
  },
};

// Vendor APIs
export const vendorAPI = {
  getAll: async () => {
    const response = await fetch(`${API_URL}/api/vendors`, {
      headers: { ...getAuthHeader() },
    });
    return handleResponse(response);
  },
  
  create: async (vendorData) => {
    const response = await fetch(`${API_URL}/api/vendors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(vendorData),
    });
    return handleResponse(response);
  },
  
  delete: async (vendorId) => {
    const response = await fetch(`${API_URL}/api/vendors/${vendorId}`, {
      method: 'DELETE',
      headers: { ...getAuthHeader() },
    });
    return handleResponse(response);
  },
};

// Stats APIs
export const statsAPI = {
  getSales: async (date = null) => {
    const url = date ? `${API_URL}/api/stats/sales?date=${date}` : `${API_URL}/api/stats/sales`;
    const response = await fetch(url, {
      headers: { ...getAuthHeader() },
    });
    return handleResponse(response);
  },
  
  getAnalytics: async (startDate = null, endDate = null) => {
    let url = `${API_URL}/api/stats/analytics`;
    const params = [];
    if (startDate) params.push(`start_date=${startDate}`);
    if (endDate) params.push(`end_date=${endDate}`);
    if (params.length > 0) url += '?' + params.join('&');
    
    const response = await fetch(url, {
      headers: { ...getAuthHeader() },
    });
    return handleResponse(response);
  },
};

export default {
  authAPI,
  userAPI,
  productAPI,
  invoiceAPI,
  sessionAPI,
  cashoutAPI,
  returnAPI,
  discardAPI,
  vendorAPI,
  statsAPI,
};
